import { useCallback, useEffect, useRef, useMemo, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { HelpdeskTicketInterface } from "shared/types/helpdesk";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { buildHelpdeskTicketParams } from "../utils/helpdeskTicketParams";

/**
 * Filter parameters for helpdesk tickets
 */
export interface HelpdeskFilters {
  search?: string;
  source?: string[];
  status?: string[];
  priority?: string[];
  building?: string[];
  unit?: string[];
  label?: string[];
  assignee?: string[];
  supplier?: string[];
  dateFilter?: 'all' | 'today' | 'this-week' | 'this-month' | 'older-than-week';
  hasUnansweredIncomingEmail?: boolean;
  hasUnreadEmails?: boolean;
}

/**
 * Hook for helpdesk ticket data using the unified GET /helpdesk/tickets endpoint.
 *
 * - In **kanban** mode: fetches all non-closed and closed tickets (pageSize=9999)
 *   via two calls to the unified endpoint.
 * - In **list** mode: skips ticket fetching (useHelpdeskListPagination handles it).
 *   Only fetches count statistics.
 *
 * ALL FILTERING AND SORTING IS DONE ON THE BACKEND.
 *
 * @param viewMode - 'list' or 'kanban'
 * @param forceTicketFetch - When true, fetches non-closed tickets even in list mode.
 *   Used by the ticket detail page so its inter-page navigation has the full
 *   non-closed set to navigate through. Defaults to false to preserve list-page
 *   optimisation (list page consumes paginated data from useHelpdeskListPagination).
 */
export const useHelpdeskDataLazy = (
  filters: HelpdeskFilters = {},
  viewMode: 'list' | 'kanban' = 'list',
  forceTicketFetch: boolean = false
) => {
  const { session } = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;

  // ── State ──────────────────────────────────────────────────────────────
  const [nonClosedTickets, setNonClosedTickets] = useState<HelpdeskTicketInterface[]>([]);
  const [closedTickets, setClosedTickets] = useState<HelpdeskTicketInterface[]>([]);
  const [isLoadingNonClosed, setIsLoadingNonClosed] = useState(false);
  const [isLoadingClosed, setIsLoadingClosed] = useState(false);

  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [openCount, setOpenCount] = useState<number | null>(null);
  const [completionCount, setCompletionCount] = useState<number | null>(null);
  const [isLoadingTotalCount, setIsLoadingTotalCount] = useState(false);

  // ── Convert filters to query-param strings ─────────────────────────────
  const filterParams = useMemo(
    () => buildHelpdeskTicketParams(filters),
    [filters],
  );

  // Stable key for dependency tracking
  const filterKey = useMemo(() => JSON.stringify(filterParams), [filterParams]);

  // ── Fetch helpers ──────────────────────────────────────────────────────

  const fetchNonClosedTickets = useCallback(async () => {
    if (!sessionRef.current) return;
    setIsLoadingNonClosed(true);
    try {
      const params = new URLSearchParams({
        status: 'open,completion',
        includeEmailCounts: 'true',
        pageSize: '9999',
        sortField: 'id',
        sortDirection: 'DESC',
        ...filterParams,
      });

      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/tickets?${params}`,
        { method: 'GET' },
        sessionRef.current,
      );

      if (!response.ok) throw new Error(`Failed to fetch non-closed tickets: ${response.status}`);

      const result = await response.json();
      setNonClosedTickets(result.data ?? []);
    } catch (error) {
      console.error('[useHelpdeskDataLazy] Error fetching non-closed tickets:', error);
      toast.error('Fout bij ophalen van tickets');
    } finally {
      setIsLoadingNonClosed(false);
    }
  }, [filterParams]);

  const fetchClosedTicketsData = useCallback(async () => {
    if (!sessionRef.current) return;
    setIsLoadingClosed(true);
    try {
      const params = new URLSearchParams({
        status: 'closed',
        includeEmailCounts: 'true',
        pageSize: '9999',
        sortField: 'id',
        sortDirection: 'DESC',
        ...filterParams,
      });

      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/tickets?${params}`,
        { method: 'GET' },
        sessionRef.current,
      );

      if (!response.ok) throw new Error(`Failed to fetch closed tickets: ${response.status}`);

      const result = await response.json();
      setClosedTickets(result.data ?? []);
    } catch (error) {
      console.error('[useHelpdeskDataLazy] Error fetching closed tickets:', error);
      toast.error('Fout bij ophalen van gesloten tickets');
    } finally {
      setIsLoadingClosed(false);
    }
  }, [filterParams]);

  // ── Fetch tickets when in kanban mode (or when fetch is forced) ────────
  // Closed tickets are only needed in kanban mode — they're not navigated to
  // from list view, so we skip that fetch even when forceTicketFetch is true.
  useEffect(() => {
    if (!session) return;
    if (viewMode !== 'kanban' && !forceTicketFetch) return;

    fetchNonClosedTickets();
    if (viewMode === 'kanban') {
      fetchClosedTicketsData();
    }
  }, [viewMode, session, filterKey, forceTicketFetch, fetchNonClosedTickets, fetchClosedTicketsData]);

  // ── Fetch total count (always, regardless of viewMode) ─────────────────
  useEffect(() => {
    if (!session) return;

    const fetchTotalCount = async () => {
      setIsLoadingTotalCount(true);
      try {
        const queryParams = new URLSearchParams(filterParams);
        const response = await fetchWithAuth(
          `${API_URL}/helpdesk/tickets/total-count?${queryParams}`,
          { method: 'GET' },
          session,
        );

        if (response.ok) {
          const data = await response.json();
          setTotalCount(data.totalCount);
          setOpenCount(data.openCount);
          setCompletionCount(data.completionCount);
        } else {
          console.error('[useHelpdeskDataLazy] Failed to fetch total count');
          toast.error('Fout bij ophalen van totaal aantal tickets');
        }
      } catch (error) {
        console.error('[useHelpdeskDataLazy] Error fetching total count:', error);
      } finally {
        setIsLoadingTotalCount(false);
      }
    };

    fetchTotalCount();
  }, [session, filterParams]);

  // ── Refresh ────────────────────────────────────────────────────────────
  const refreshTickets = useCallback(async () => {
    await Promise.all([
      fetchNonClosedTickets(),
      fetchClosedTicketsData(),
    ]);
  }, [fetchNonClosedTickets, fetchClosedTicketsData]);

  // ── Action handlers ────────────────────────────────────────────────────

  const handleCompleteTicket = useCallback(async (ticketId: number) => {
    if (!session) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${ticketId}/complete`,
        { method: 'PUT' },
        session,
      );

      if (response.ok) {
        await refreshTickets();
        toast.success('Ticket succesvol voltooid');
      } else {
        throw new Error('Failed to complete ticket');
      }
    } catch (error) {
      console.error('Error completing ticket:', error);
      toast.error('Fout bij voltooien van ticket');
    }
  }, [session, refreshTickets]);

  const handleMoveToCompletion = useCallback(async (ticketId: number) => {
    if (!session) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${ticketId}/completion-phase`,
        { method: 'PUT' },
        session,
      );

      if (response.ok) {
        const updatedTicket = await response.json();

        // Optimistic update: update the ticket's status in the non-closed array
        setNonClosedTickets(prev =>
          prev.map(t => t.id === ticketId ? { ...t, ...updatedTicket } : t)
        );

        toast.success('Ticket verplaatst naar afrondingsfase');
      } else {
        throw new Error('Failed to move ticket to completion');
      }
    } catch (error) {
      console.error('Error moving ticket to completion:', error);
      toast.error('Fout bij verplaatsen van ticket naar afrondingsfase');
    }
  }, [session]);

  const handleRevertToOpen = useCallback(async (ticketId: number) => {
    if (!session) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${ticketId}/revert-to-open`,
        { method: 'PUT' },
        session,
      );

      if (response.ok) {
        const updatedTicket = await response.json();

        // Optimistic update: update the ticket's status in the non-closed array
        setNonClosedTickets(prev =>
          prev.map(t => t.id === ticketId ? { ...t, ...updatedTicket } : t)
        );

        toast.success('Ticket teruggezet naar openstaand');
      } else {
        throw new Error('Failed to revert ticket to open');
      }
    } catch (error) {
      console.error('Error reverting ticket to open:', error);
      toast.error('Fout bij terugzetten van ticket');
    }
  }, [session]);

  const handleUpdateTicket = useCallback((ticketId: number, updates: any) => {
    const updateFn = (ticket: HelpdeskTicketInterface) =>
      ticket.id === ticketId ? { ...ticket, ...updates } : ticket;

    setNonClosedTickets(prev => prev.map(updateFn));
    setClosedTickets(prev => prev.map(updateFn));
  }, []);

  return {
    // Ticket data
    nonClosedTickets,
    closedTickets,
    setNonClosedTickets,
    setClosedTickets,
    isLoadingTickets: isLoadingNonClosed || isLoadingClosed,

    // Count data
    totalCount,
    openCount,
    completionCount,
    isLoadingTotalCount,

    // Actions
    refreshTickets,
    handleCompleteTicket,
    handleMoveToCompletion,
    handleRevertToOpen,
    handleUpdateTicket,
  };
};
